import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, InputNumber, Modal, Radio, Switch, Tooltip } from 'antd';
import { debounce } from 'lodash';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
    ClockIcon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    StatusBadge,
} from '../../components/ui';
import {
    ADDITIONAL_PAGE_SIZE,
    apiFieldErrors,
    createAdditionalService,
    deleteAdditionalService,
    fetchAdditionalServiceHistory,
    fetchAdditionalServices,
    firstApiError,
    formatServicePrice,
    updateAdditionalService,
} from '../../services/additionalServiceService';

const EMPTY_FORM = {
    name: '',
    price: '',
    description: '',
    prefix: '',
    time: 10,
    type: 'all',
};

const HISTORY_FIELDS = ['name', 'price', 'description', 'prefix', 'time', 'type', 'deleted_at'];

function toastByStatus(error, t) {
    const code = error?.response?.status;
    if (code === 401) return;
    if (code === 403) toast.error(t('additionalServices.forbidden'));
    else if (code === 404) toast.error(t('additionalServices.notFound'));
    else toast.error(firstApiError(error) || t('additionalServices.saveError'));
}

function formatWhen(value, dash) {
    if (!value) return dash;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : value;
}

function personName(person, dash) {
    if (!person) return dash;
    return person.name || dash;
}

export default function AdditionalServices({ embedded = false }) {
    const { t } = useTranslation();
    const dash = t('common.dash');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: ADDITIONAL_PAGE_SIZE,
        total: 0,
    });
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [fieldErrors, setFieldErrors] = useState({});
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyItem, setHistoryItem] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyPagination, setHistoryPagination] = useState({
        current: 1,
        pageSize: ADDITIONAL_PAGE_SIZE,
        total: 0,
    });

    const applySearch = useMemo(
        () =>
            debounce((value) => {
                setSearch(value.trim().slice(0, 100));
                setPagination((prev) => ({ ...prev, current: 1 }));
            }, 400),
        []
    );

    useEffect(() => () => applySearch.cancel(), [applySearch]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchAdditionalServices({
                search,
                includeDeleted,
                page: pagination.current,
                perPage: pagination.pageSize,
            });
            setItems(result.items);
            setPagination((prev) => ({
                ...prev,
                current: result.pagination.current,
                pageSize: result.pagination.pageSize,
                total: result.pagination.total,
            }));
        } catch (error) {
            toastByStatus(error, t);
        } finally {
            setLoading(false);
        }
    }, [search, includeDeleted, pagination.current, pagination.pageSize, t]);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setFieldErrors({});
        setModalOpen(true);
    };

    const openEdit = (record) => {
        if (record.deleted_at) return;
        setEditing(record);
        setForm({
            name: record.name ?? '',
            price: record.price ?? '',
            description: record.description ?? '',
            prefix: record.prefix ?? '',
            time: record.time ?? 10,
            type: record.type || 'all',
        });
        setFieldErrors({});
        setModalOpen(true);
    };

    const buildBody = () => {
        const body = {
            name: form.name.trim(),
            price: Number(form.price).toFixed(2),
            type: form.type || 'all',
        };
        if (form.description.trim()) body.description = form.description.trim();
        else if (editing) body.description = null;
        if (form.prefix.trim()) body.prefix = form.prefix.trim().slice(0, 4);
        if (form.time) body.time = Number(form.time);
        return body;
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            setFieldErrors({ name: t('additionalServices.nameRequired') });
            return;
        }
        if (form.price === '' || form.price == null || Number.isNaN(Number(form.price))) {
            setFieldErrors({ price: t('additionalServices.priceRequired') });
            return;
        }
        setSaving(true);
        setFieldErrors({});
        try {
            if (editing) await updateAdditionalService(editing.id, buildBody());
            else await createAdditionalService(buildBody());
            toast.success(editing ? t('additionalServices.updated') : t('additionalServices.created'));
            setModalOpen(false);
            load();
        } catch (error) {
            setFieldErrors(apiFieldErrors(error));
            toastByStatus(error, t);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (record) => {
        if (record.deleted_at) return;
        Modal.confirm({
            title: t('additionalServices.deleteConfirm'),
            okText: t('common.delete'),
            cancelText: t('common.cancel'),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await deleteAdditionalService(record.id);
                    toast.success(t('additionalServices.deleted'));
                    load();
                } catch (error) {
                    toastByStatus(error, t);
                }
            },
        });
    };

    const loadHistory = async (record, page = 1) => {
        setHistoryItem(record);
        setHistoryOpen(true);
        setHistoryLoading(true);
        try {
            const result = await fetchAdditionalServiceHistory(record.id, { page, perPage: historyPagination.pageSize });
            setHistoryRows(result.items);
            setHistoryPagination(result.pagination);
        } catch (error) {
            toastByStatus(error, t);
        } finally {
            setHistoryLoading(false);
        }
    };

    const columns = [
        {
            title: t('common.id'),
            dataIndex: 'id',
            key: 'id',
            width: 72,
            render: (id) => <span className="text-xs tabular-nums text-slate-500">{id}</span>,
        },
        {
            title: t('additionalServices.name'),
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <span className={`text-sm font-medium ${record.deleted_at ? 'text-slate-400' : 'text-slate-800'}`}>
                    {name}
                </span>
            ),
        },
        {
            title: t('additionalServices.price'),
            dataIndex: 'price',
            key: 'price',
            width: 130,
            render: (price) => (
                <span className="text-sm tabular-nums text-slate-700">{formatServicePrice(price)}</span>
            ),
        },
        {
            title: t('additionalServices.author'),
            dataIndex: 'created_by',
            key: 'created_by',
            width: 160,
            render: (createdBy) => <span className="text-sm text-slate-600">{personName(createdBy, dash)}</span>,
        },
        {
            title: t('common.status'),
            key: 'deleted_at',
            width: 120,
            render: (_, record) =>
                record.deleted_at ? (
                    <StatusBadge tone="muted">{t('additionalServices.archived')}</StatusBadge>
                ) : (
                    <StatusBadge tone="green">{t('additionalServices.active')}</StatusBadge>
                ),
        },
        {
            title: t('common.actions'),
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <div className="flex items-center gap-0.5">
                    <Tooltip title={t('additionalServices.history')}>
                        <RowIconButton onClick={() => loadHistory(record)}>
                            <ClockIcon className="h-[18px] w-[18px]" />
                        </RowIconButton>
                    </Tooltip>
                    {!record.deleted_at && (
                        <>
                            <Tooltip title={t('common.edit')}>
                                <RowIconButton onClick={() => openEdit(record)}>
                                    <PencilSquareIcon className="h-[18px] w-[18px]" />
                                </RowIconButton>
                            </Tooltip>
                            <Tooltip title={t('common.delete')}>
                                <RowIconButton onClick={() => handleDelete(record)}>
                                    <TrashIcon className="h-[18px] w-[18px]" />
                                </RowIconButton>
                            </Tooltip>
                        </>
                    )}
                </div>
            ),
        },
    ];

    const historyColumns = [
        {
            title: t('additionalServices.action'),
            dataIndex: 'action',
            key: 'action',
            width: 140,
            render: (action) => t(`additionalServices.action_${action}`, { defaultValue: action }),
        },
        {
            title: t('additionalServices.performedBy'),
            dataIndex: 'performed_by',
            key: 'performed_by',
            width: 160,
            render: (person) => personName(person, dash),
        },
        {
            title: t('additionalServices.changes'),
            key: 'diff',
            render: (_, record) => {
                if (record.action === 'created') {
                    return (
                        <span className="text-sm text-slate-600">
                            {record.after?.name} · {formatServicePrice(record.after?.price)}
                        </span>
                    );
                }
                const rows = HISTORY_FIELDS.filter((key) => {
                    const before = record.before?.[key];
                    const after = record.after?.[key];
                    return String(before ?? '') !== String(after ?? '');
                });
                if (!rows.length) return <span className="text-xs text-slate-400">{dash}</span>;
                return (
                    <div className="space-y-1">
                        {rows.map((key) => (
                            <div key={key} className="text-xs text-slate-600">
                                <span className="font-semibold">{t(`additionalServices.field_${key}`)}:</span>{' '}
                                <span className="text-slate-400">{String(record.before?.[key] ?? dash)}</span>
                                {' → '}
                                <span>{String(record.after?.[key] ?? dash)}</span>
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: t('common.date'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (value) => <span className="text-sm tabular-nums">{formatWhen(value, dash)}</span>,
        },
    ];

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#1D2C7B]">{record.name}</span>
                    <span className="mt-0.5 block text-xs tabular-nums text-slate-500">
                        {formatServicePrice(record.price)}
                    </span>
                </div>
                {record.deleted_at ? (
                    <StatusBadge tone="muted">{t('additionalServices.archived')}</StatusBadge>
                ) : (
                    <StatusBadge tone="green">{t('additionalServices.active')}</StatusBadge>
                )}
            </div>
            <div className="mt-2.5 flex items-center gap-1">
                <RowIconButton onClick={() => loadHistory(record)} aria-label={t('additionalServices.history')}>
                    <ClockIcon className="h-[18px] w-[18px]" />
                </RowIconButton>
                {!record.deleted_at && (
                    <>
                        <RowIconButton onClick={() => openEdit(record)} aria-label={t('common.edit')}>
                            <PencilSquareIcon className="h-[18px] w-[18px]" />
                        </RowIconButton>
                        <RowIconButton onClick={() => handleDelete(record)} aria-label={t('common.delete')}>
                            <TrashIcon className="h-[18px] w-[18px]" />
                        </RowIconButton>
                    </>
                )}
            </div>
        </div>
    );

    const body = (
        <>
                <PageToolbar
                    title={embedded ? undefined : t('additionalServices.title')}
                    count={pagination.total}
                    filters={
                        <>
                            <Input
                                allowClear
                                size="small"
                                maxLength={100}
                                prefix={<MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />}
                                placeholder={t('additionalServices.search')}
                                value={searchInput}
                                onChange={(e) => {
                                    setSearchInput(e.target.value);
                                    applySearch(e.target.value);
                                }}
                                className="md:!w-[240px]"
                            />
                            <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                                <Switch
                                    size="small"
                                    checked={includeDeleted}
                                    onChange={(checked) => {
                                        setIncludeDeleted(checked);
                                        setPagination((prev) => ({ ...prev, current: 1 }));
                                    }}
                                />
                                {t('additionalServices.includeDeleted')}
                            </label>
                        </>
                    }
                    actions={
                        <ActionButton icon={<PlusIcon className="h-4 w-4" />} onClick={openCreate}>
                            {t('additionalServices.add')}
                        </ActionButton>
                    }
                />

                <DataTable
                    rowKey="id"
                    columns={columns}
                    dataSource={items}
                    loading={loading}
                    mobileCard={renderMobileCard}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                    }}
                    onChange={(next) =>
                        setPagination((prev) => ({ ...prev, current: next.current, pageSize: next.pageSize }))
                    }
                    scrollX={900}
                />

            <Modal
                title={editing ? t('additionalServices.edit') : t('additionalServices.create')}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={handleSave}
                confirmLoading={saving}
                okText={t('common.save')}
                cancelText={t('common.cancel')}
                centered
                width="100%"
                style={{ maxWidth: 520 }}
                destroyOnClose
            >
                <div className="space-y-3">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('additionalServices.name')}</label>
                        <Input
                            maxLength={40}
                            value={form.name}
                            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                        />
                        {fieldErrors.name && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.name}</p>}
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('additionalServices.price')}</label>
                        <InputNumber
                            className="w-full"
                            min={0}
                            max={9999999999.99}
                            step={0.01}
                            precision={2}
                            value={form.price === '' ? null : Number(form.price)}
                            onChange={(value) => setForm((prev) => ({ ...prev, price: value ?? '' }))}
                        />
                        {fieldErrors.price && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.price}</p>}
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('additionalServices.description')}</label>
                        <Input.TextArea
                            rows={3}
                            maxLength={2000}
                            value={form.description}
                            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                        />
                        {fieldErrors.description && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.description}</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">{t('additionalServices.prefix')}</label>
                            <Input
                                maxLength={4}
                                value={form.prefix}
                                onChange={(e) => setForm((prev) => ({ ...prev, prefix: e.target.value }))}
                            />
                            {fieldErrors.prefix && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.prefix}</p>}
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">{t('additionalServices.time')}</label>
                            <InputNumber
                                className="w-full"
                                min={1}
                                max={1440}
                                value={form.time}
                                onChange={(value) => setForm((prev) => ({ ...prev, time: value || 10 }))}
                            />
                            {fieldErrors.time && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.time}</p>}
                        </div>
                    </div>
                    <Radio.Group
                        buttonStyle="solid"
                        value={form.type}
                        onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
                    >
                        <Radio.Button value="all">{t('status.all')}</Radio.Button>
                        <Radio.Button value="ambulatory">{t('status.ambulatory')}</Radio.Button>
                        <Radio.Button value="stationary">{t('status.stationary')}</Radio.Button>
                    </Radio.Group>
                    {fieldErrors.type && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.type}</p>}
                </div>
            </Modal>

            <Modal
                title={historyItem ? `${t('additionalServices.history')} · ${historyItem.name}` : t('additionalServices.history')}
                open={historyOpen}
                onCancel={() => setHistoryOpen(false)}
                footer={null}
                width="100%"
                style={{ maxWidth: 860 }}
                centered
                destroyOnClose
            >
                {historyItem?.created_by && (
                    <p className="mb-3 text-sm text-slate-500">
                        {t('additionalServices.createdBy')}: <strong>{personName(historyItem.created_by, dash)}</strong>
                    </p>
                )}
                <DataTable
                    rowKey="id"
                    columns={historyColumns}
                    dataSource={historyRows}
                    loading={historyLoading}
                    pagination={{
                        ...historyPagination,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                    }}
                    onChange={(next) => historyItem && loadHistory(historyItem, next.current)}
                    scrollX={720}
                />
            </Modal>
        </>
    );

    if (embedded) return body;

    return (
        <PageShell>
            <PageCard>{body}</PageCard>
        </PageShell>
    );
}
